import hoistNonReactStatics from 'hoist-non-react-statics';
import { NextAuthenticatedPageProps } from 'next';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Actions, Subjects, unpackAbilities } from 'context/user/UserContext';
import EthicalPrompt from '../components/EthicalPrompt';
import {
  UserContextQuery,
  useUserContextQuery,
} from '../context/user/operation.generated';
import { RE_AUTHENTICATION_LINK_IGNORE_FLAG } from './initApollo';
import {
  SubscriptionStatus,
  TeamRole,
} from '../../server/graphql/__generated.types';
import {
  showZendeskWebWidget,
  setZendeskConversationTags,
  hideZendeskWebWidget,
} from '../services/zendesk';

const { _ENV } = getConfig().publicRuntimeConfig;

type WithAuthenticatedUserOptions = {
  userState?: 'verified' | 'subscribed';
};

export const getUserRedirectDestination = ({
  user: {
    id: userId,
    isVerified,
    roles,
    pendingTeamInvitation,
    subscription,
    team,
  },
  userState,
}: {
  user: NextAuthenticatedPageProps['user'];
} & WithAuthenticatedUserOptions): string | null => {
  const isUserSubscribed = isSubscribed({
    userId,
    userSubscription: subscription,
    team,
    roles,
  });
  // NOTE: only checking for pending invitation if the userState is defined, otherwise it is
  // assumed the user is still in the signup flow (ie not verified and/or approved)
  if (userState && pendingTeamInvitation) {
    return `/auth/invitation/join_team?invitation_code=${pendingTeamInvitation.invitationCode}`;
  }

  switch (userState) {
    case 'verified':
      if (!isVerified) {
        return '/auth/account/verify';
      }
      break;
    case 'subscribed':
      if (!isVerified) {
        return '/auth/account/verify';
      }
      if (!isUserSubscribed) {
        return '/auth/account/new_user';
      }
      if (!subscription) {
        return '/dashboard/account/subscription?prompt=upgrade';
      }
      break;
    default:
      break;
  }
  return null;
};

function isSubscriptionActive(
  subscription: UserContextQuery['user']['subscription']
): boolean {
  return Boolean(
    subscription &&
      subscription.status !== SubscriptionStatus.Canceled &&
      subscription.status !== SubscriptionStatus.IncompleteExpired
  );
}

function isSubscribed(inputs: {
  userId: string;
  userSubscription: NextAuthenticatedPageProps['user']['subscription'];
  team: NextAuthenticatedPageProps['user']['team'];
  roles: NextAuthenticatedPageProps['user']['roles'];
}): boolean {
  const { userId, userSubscription, team, roles } = inputs;

  let hasFallbackTrialSubscription = false;
  if (userSubscription?.id.startsWith(userId)) {
    // It's a fallback trial subscription. If the user doesn't have a role, we can assume that they haven't
    // subscribed yet. Note that in the new system, the user needs an actual subscription object in the DB
    // to be considered a trial user.
    hasFallbackTrialSubscription = true;
  }

  const hasActiveSubscription = team
    ? isSubscriptionActive(team.subscription)
    : isSubscriptionActive(userSubscription);

  return (
    roles.length > 0 || (!hasFallbackTrialSubscription && hasActiveSubscription)
  );
}

type ZendeskTaggablePlan = NonNullable<
  NonNullable<NonNullable<UserContextQuery['user']>['subscription']>['plan']
>;

/**
 * Builds the conversation tags Zendesk shows support alongside a chat. Kept as a pure function so
 * the tag vocabulary support depends on is covered by tests rather than only asserted by hand.
 */
export function getZendeskConversationTags(
  plan: ZendeskTaggablePlan,
  env: string
): string[] {
  const tags = [
    `planName:${plan.displayName}`,
    `environment:${env}`,
    `interval:${plan.interval}`,
    `isManaged:${plan.managed}`,
    `isMultiSeat:${plan.isMultiSeat}`,
    `isTrial:${plan.isTrial}`,
    `planId:${plan.id}`,
    `tier:${plan.tier}`,
  ];

  // TODO: Remove these two tag formats once Zendesk's triggers and views have been moved to the
  // `tier:`/`isTrial:` formats above. Dropping them early silently breaks any automation keyed on
  // them, and nothing on our side reports it.
  tags.push(`${plan.tier}User`);
  // Explicitly adding trialUser to cover designer trials.
  if (plan.isTrial) tags.push('trialUser');

  return tags;
}

/**
 * Restrict page access to authenticated user's.
 *
 * @example
 * // Restrict page to authenticated user's that are subscribed to the product (trial/paid)
 * withAuthenticatedUser(DashboardPage, { userState: 'subscribed' })
 *
 * @example
 * // Restrict page to authenticated WellSaid admin's
 * withAuthenticatedUser(AdminPage, { userRole: 'admin' })
 */

/**
 * Client-Side Rendering (CSR) version of withAuthenticatedUser.
 * This skips Next.js SSR and fetches the user session directly in the browser.
 */
export function withAuthenticatedUser(
  PageComponent: React.ComponentType<any>,
  options: WithAuthenticatedUserOptions = {}
) {
  const WithAuthenticatedUser = (props: any) => {
    const router = useRouter();
    const { data, loading } = useUserContextQuery({
      errorPolicy: 'ignore',
      fetchPolicy: 'cache-first',
      context: {
        [RE_AUTHENTICATION_LINK_IGNORE_FLAG]: true,
      },
    });

    const user = data?.user;
    const destination = user
      ? getUserRedirectDestination({ user, ...options })
      : null;

    React.useEffect(() => {
      // We only execute redirects once the graphql query completes and we are inside the browser
      if (!loading && router?.isReady) {
        if (!user) {
          router?.replace(
            `/auth/sign_in_redirect${
              router?.asPath
                ? `?redirect_to=${encodeURIComponent(router.asPath)}`
                : ''
            }`
          );
          return;
        }

        if (destination && destination !== router?.asPath) {
          router?.replace(destination);
        }
      }
    }, [loading, user, router, destination]);

    const env = _ENV || 'local';

    const subscriptionPlan = user?.subscription?.plan;

    const hasSubscribed = user
      ? isSubscribed({
          userId: user.id,
          userSubscription: user.subscription,
          team: user.team,
          roles: user.roles,
        })
      : false;

    const ability = user ? unpackAbilities(user.abilities) : null;
    const showSupportChat = user?.isRBACEnabled
      ? user.team &&
        user.team.subscription?.plan?.isMultiSeat &&
        ability?.can(Actions.Create, Subjects.SupportChat) &&
        ability?.can(Actions.Read, Subjects.SupportChat)
      : user?.team?.viewersRoleOnTeam !== TeamRole.Collaborator;

    React.useEffect(() => {
      // Only show Zendesk chat widget once user has a valid subscription and has accepted TOS.
      if (user?.hasAcceptedTerms && hasSubscribed && subscriptionPlan) {
        setZendeskConversationTags(
          getZendeskConversationTags(subscriptionPlan, env)
        );

        if (showSupportChat) {
          showZendeskWebWidget();
        }
      }

      if (!showSupportChat) hideZendeskWebWidget();
    }, [
      user?.hasAcceptedTerms,
      hasSubscribed,
      subscriptionPlan,
      showSupportChat,
      env,
    ]);

    if (loading || !user || (destination && destination !== router?.asPath)) {
      // Display a full-page loading state while verifying auth session on the client
      return (
        <Box
          sx={{
            display: 'flex',
            width: '100vw',
            height: '100vh',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress size="3rem" />
        </Box>
      );
    }

    const { hasAcceptedTerms } = user;

    const pagePropsWithUser = { ...props, user };

    return !hasAcceptedTerms && user.isVerified && hasSubscribed ? (
      <EthicalPrompt>
        <PageComponent {...pagePropsWithUser} />
      </EthicalPrompt>
    ) : (
      <PageComponent {...pagePropsWithUser} />
    );
  };

  WithAuthenticatedUser.displayName = `withAuthenticatedUser(${
    PageComponent.displayName || PageComponent.name || 'Component'
  })`;

  // Note: We deliberately drop getInitialProps from PageComponent by excluding it.
  // We also intercept Layout to prevent layout flashing during the CSR user fetch.
  hoistNonReactStatics(WithAuthenticatedUser, PageComponent, {
    getInitialProps: true,
    Layout: true,
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const PageLayout = (PageComponent as any).Layout;

  if (PageLayout) {
    const ClientLayoutHOC = ({ children }: { children: React.ReactNode }) => {
      const { data, loading } = useUserContextQuery({
        errorPolicy: 'ignore',
        fetchPolicy: 'cache-first',
        context: {
          [RE_AUTHENTICATION_LINK_IGNORE_FLAG]: true,
        },
      });
      const user = data?.user;
      const destination = user
        ? getUserRedirectDestination({ user, ...options })
        : null;

      if (loading || !user || destination) {
        // eslint-disable-next-line react/jsx-no-useless-fragment
        return <>{children}</>;
      }

      return <PageLayout>{children}</PageLayout>;
    };

    WithAuthenticatedUser.Layout = ClientLayoutHOC;
  }

  return WithAuthenticatedUser;
}

export const withSubscribedUser = (
  PageComponent: React.ComponentType<any>,
  options: WithAuthenticatedUserOptions = {}
) =>
  withAuthenticatedUser(PageComponent, {
    userState: 'subscribed',
    ...options,
  });

export const withVerifiedUser = (PageComponent: React.ComponentType<any>) =>
  withAuthenticatedUser(PageComponent, { userState: 'verified' });
